"use client";
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Card, Flex, Collapse, Form, App, Divider, Alert, Tag } from "antd";
import { ENDPOINTS } from "@/constants/endpoint";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import { usercontext } from "@/lib/context";

import { Input, Button } from "antd";
import GSTDetails from "./GSTDetails";
import PriceDetails from "./PriceDetails";
import { formatCurrency } from "@/lib/formatCurrency";
import { IconRosetteDiscount, IconRosetteDiscountFilled } from "@/lib/icons";
import ReviewSkeleton from "./ReviewSkeleton";
import { normalizeCalculationResponse } from "@/lib/normalizeCalculation";
import { discountCodeValidation } from "@/lib/validations";

import { setStoredSessionId } from "@/lib/seatSession";
import SessionTimer from "./SessionTimer";
import { trackBeginCheckout } from "@/lib/analytics";
import { trackInitiateCheckout } from "@/lib/metaPixel";

const Review = ({
  data,
  orderDetails,
  setOrderDetails,
  setParentLoading,
  onSessionExpire,
}) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const { user } = useContext(usercontext);
  const [form] = Form.useForm();
  const [discounts, setDiscounts] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(false);
  const discount = Form.useWatch("discount", form);
  const getCalculationRef = useRef(null);
  const hasTrackedCheckoutRef = useRef(false);

  const qty = orderDetails?.ticket?.qty || 1;
  const unitPrice = orderDetails?.ticket?.price || 0;
  const totalTicketBase = qty * unitPrice;

  const fallbackAmounts = {
    subtotal:
      orderDetails?.subtotal ??
      (totalTicketBase > 0 ? totalTicketBase : unitPrice),
    total_after_tax:
      orderDetails?.total_amount ??
      (totalTicketBase > 0 ? totalTicketBase : unitPrice),
  };

  useEffect(() => {
    if (hasTrackedCheckoutRef.current || !orderDetails?.ticket?.event_id)
      return;
    hasTrackedCheckoutRef.current = true;

    const ticket = orderDetails.ticket;
    const total = orderDetails.total_amount ?? fallbackAmounts.total_after_tax;

    trackBeginCheckout({
      event_id: ticket.event_id,
      event_name: ticket.event_name,
      ticket_id: ticket.id,
      ticket_name: ticket.name,
      quantity: ticket.qty || 1,
      value: total,
      currency: ticket.currency || "INR",
      coupon: orderDetails.discount,
    });

    trackInitiateCheckout({
      eventId: ticket.event_id,
      eventName: ticket.event_name || ticket.name,
      ticketId: ticket.id,
      ticketName: ticket.name,
      ticketCount: ticket.qty || 1,
      totalAmount: total,
      currency: ticket.currency || "INR",
    });
  }, [orderDetails?.ticket?.event_id]);

  const getDiscounts = async () => {
    try {
      setDiscountLoading(true);
      const discountEventId =
        data?.id ||
        orderDetails?.parent_event_id ||
        orderDetails?.ticket?.event_id;
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/${ENDPOINTS.DISCOUNT_LIST}?event_id=${discountEventId}&ticket_id=${orderDetails?.ticket?.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...DEV_AUTH_HEADERS,
          },
        },
      );
      const res = await response.json();
      if (res?.status) {
        setDiscounts(res?.data || []);
      }
    } catch (error) {
      console.error("Error fetching discounts:", error);
    } finally {
      setDiscountLoading(false);
    }
  };

  const getCalculation = useCallback(
    async (discount = "") => {
      // For calculation, we must pass the child event ID
      const calculationEventId =
        orderDetails?.ticket?.event_id || orderDetails?.event_id || data?.id;

      if (!calculationEventId || !orderDetails?.ticket?.id) {
        return;
      }

      const seatIds = (orderDetails?.ticket?.seat_id || []).filter(Boolean);

      try {
        setParentLoading(true);
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/${ENDPOINTS.CALCULATION}`,
          {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers: {
              "Content-Type": "application/json",
              ...DEV_AUTH_HEADERS,
            },
            body: JSON.stringify({
              event_id: calculationEventId,
              seat_id: seatIds,
              session_id: orderDetails?.session_id || null,
              customer_id: user?.id || null,
              tickets: [
                {
                  ticket_id: orderDetails.ticket.id,
                  quantity: orderDetails.ticket.qty,
                },
              ],
              coupon_code: discount,
            }),
          },
        );

        const res = await response.json();

        if (res?.status && res?.data) {
          const normalized = normalizeCalculationResponse(res.data);

          const sessionId =
            normalized?.session_id ||
            res?.data?.[0]?.session_id ||
            orderDetails?.session_id;

          if (sessionId && orderDetails?.ticket?.event_id) {
            setStoredSessionId(orderDetails.ticket.event_id, sessionId);
          }

          const hasValidDiscount =
            normalized?.discount_amount &&
            Number(normalized.discount_amount) > 0;

          if (discount && !hasValidDiscount) {
            form.setFieldsValue({ discount: "" });
            message.error(
              res?.error || res?.message || "Invalid discount code",
            );
          }

          setCalculation(normalized);

          setOrderDetails((prev) => ({
            ...prev,
            subtotal:
              normalized?.subtotal ??
              (prev?.ticket?.qty
                ? prev.ticket.qty * prev.ticket.price
                : prev?.ticket?.price) ??
              prev?.subtotal,
            total_amount:
              normalized?.total_after_tax ??
              normalized?.subtotal ??
              (prev?.ticket?.qty
                ? prev.ticket.qty * prev.ticket.price
                : prev?.ticket?.price) ??
              prev?.total_amount,
            user_id: user?.id,
            session_id: sessionId || prev?.session_id,
            discount: hasValidDiscount ? discount : "",
          }));
        } else {
          if (discount) {
            form.setFieldsValue({ discount: "" });
            message.error(
              res?.error || res?.message || "Invalid discount code",
            );
            if (!calculation) {
              getCalculation("");
            }
          } else {
            setCalculation(null);
            setError(res?.error || res?.message || "Failed to calculate price");
            message.error(
              res?.error || res?.message || "Failed to calculate price",
            );

            const q = orderDetails?.ticket?.qty || 1;
            const p = orderDetails?.ticket?.price || 0;
            const base = q * p;

            setOrderDetails((prev) => ({
              ...prev,
              subtotal: base || prev?.subtotal,
              total_amount: base || prev?.total_amount,
              discount: "",
            }));
          }
        }
      } catch (error) {
        setCalculation(null);
        setError(error?.message || "Something went wrong");

        if (discount) {
          form.setFieldsValue({ discount: "" });
        }

        message.error(error?.message || "Something went wrong");
      } finally {
        setLoading(false);
        setParentLoading(false);
      }
    },
    [
      data?.id,
      orderDetails?.parent_event_id,
      orderDetails?.ticket,
      orderDetails?.session_id,
      user?.id,
      setOrderDetails,
      setParentLoading,
      form,
    ],
  );

  // Keep ref in sync
  useEffect(() => {
    getCalculationRef.current = getCalculation;
  }, [getCalculation]);

  // Fire once per meaningful ticket change — never re-fires because getCalculation changed
  useEffect(() => {
    if (!orderDetails?.ticket?.event_id) {
      setLoading(false);
      return;
    }
    getCalculationRef.current?.("");
    getDiscounts();
  }, [
    orderDetails?.ticket?.event_id,
    orderDetails?.ticket?.id,
    orderDetails?.ticket?.qty,
    orderDetails?.ticket?.seat_id?.length,
  ]);

  const displaySubtotal = calculation?.subtotal ?? fallbackAmounts.subtotal;
  const displayTotal =
    calculation?.total_after_tax ?? fallbackAmounts.total_after_tax;
  const isFreeTicket = Number(displaySubtotal) === 0;

  if (!orderDetails?.ticket) {
    return (
      <Alert
        type="info"
        showIcon
        message="No ticket selected"
        description="Please go back and select your seats or tickets."
      />
    );
  }

  if (loading) return <ReviewSkeleton />;

  return (
    <Flex vertical className="my-[35px]! flex flex-col" gap={4}>
      {orderDetails?.session_id && (
        <SessionTimer
          createdAt={orderDetails?.session_created_at}
          onExpire={onSessionExpire}
        />
      )}
      {error && !calculation && (
        <Alert
          type="warning"
          showIcon
          // message="Could not refresh price from server"
          description={` ${error} Showing estimated total.`}
          className="mb-2"
        />
      )}

      <PriceDetails
        title={"Subtotal"}
        currency={orderDetails?.ticket?.currency}
        amount={displaySubtotal}
      />

      {calculation?.discount_amount ? (
        <PriceDetails
          title={"Discount"}
          currency={orderDetails?.ticket?.currency}
          amount={calculation?.discount_amount}
          showMinus
        />
      ) : null}

      {calculation?.ticket_tax_details ? (
        <GSTDetails
          ticket_tax_details={calculation?.ticket_tax_details}
          currency={orderDetails?.ticket?.currency}
        />
      ) : null}

      {calculation?.convenience_fee ? (
        <div className="conv-fee-collapse">
          <Collapse
            bordered={false}
            size="small"
            items={[
              {
                key: "1",
                label: (
                  <PriceDetails
                    title={"Convenience Fee"}
                    currency={orderDetails.ticket.currency}
                    amount={calculation?.convenience_fee}
                    size="12px"
                    padding={false}
                  />
                ),
                children: (
                  <>
                    <PriceDetails
                      title={"Base Fee"}
                      currency={orderDetails.ticket.currency}
                      amount={calculation?.platform_fee_base}
                      size="12px"
                      padding={false}
                    />
                    {calculation.platform_fee_tax_details && (
                      <GSTDetails
                        ticket_tax_details={
                          calculation.platform_fee_tax_details
                        }
                        currency={orderDetails?.ticket?.currency}
                        size="12px"
                        padding={false}
                      />
                    )}
                  </>
                ),
              },
            ]}
          />
        </div>
      ) : null}

      <Divider />
      <PriceDetails
        title={"Total Amount"}
        currency={orderDetails?.ticket?.currency}
        amount={displayTotal}
        size="20px"
      />
      <Divider />

      {!isFreeTicket && calculation?.discount_amount ? (
        <Alert
          message={
            "Discount code " +
            (calculation?.discount?.code
              ? "'" + calculation?.discount?.code + "' "
              : "") +
            "applied"
          }
          description={
            calculation?.discount?.description ??
            "Discount of " +
              formatCurrency(
                calculation?.discount_amount ?? 0,
                orderDetails?.ticket?.currency ?? "INR",
              )
          }
          type="success"
          showIcon
          icon={<IconRosetteDiscountFilled size={32} />}
          action={
            <Button
              variant="text"
              color="danger"
              size="small"
              onClick={() => {
                getCalculation("");
                form.resetFields();
              }}
            >
              Remove
            </Button>
          }
        />
      ) : !isFreeTicket ? (
        <Card title="Apply discount" size="small">
          <p className="text-grey-500 mb-4!">
            Got a discount code? Pop it in below and unlock some khaas
            discounts! Because who doesn&apos;t love saving a little extra on
            epic experiences?
          </p>
          <Form
            form={form}
            onFinish={(values) => {
              getCalculation(values.discount);
            }}
          >
            <Flex gap={10}>
              <Form.Item
                name="discount"
                className="w-full"
                rules={discountCodeValidation()}
              >
                <Input
                  size="large"
                  placeholder="Discount Code"
                  maxLength={30}
                />
              </Form.Item>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                disabled={!discount}
              >
                Apply
              </Button>
            </Flex>
          </Form>

          {discounts.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold mb-2 text-gray-400 uppercase tracking-wider">
                Available Offers
              </p>
              <Flex vertical gap={8}>
                {discounts.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5"
                  >
                    <IconRosetteDiscount
                      size={20}
                      className="text-orange-400 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <Tag color="green">{item.coupon_code}</Tag>
                      {item.description && (
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <Button
                      type="link"
                      size="small"
                      className="!text-[#021526] !font-semibold shrink-0"
                      onClick={() => {
                        form.setFieldsValue({ discount: item.coupon_code });
                        getCalculation(item.coupon_code);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                ))}
              </Flex>
            </div>
          )}
        </Card>
      ) : null}
    </Flex>
  );
};

export default Review;
